import feathersClient from '@/feathers-client'

/**
 * Gets user ratings for multiple compassCategories in a compass
 * @param {number} frameworkId - The ID of the framework
 * @param {string|number} compassId - The ID of the compass
 * @param {Array} compassCategories - Array of category objects
 * @returns {Promise<Array<number>>} Array of category ratings
 */
export const getCategoryUserRatings = async (frameworkId, compassId, compassCategories) => {
  // Validate required parameters
  if (!compassId || !frameworkId || !compassCategories || compassCategories.length === 0) {
    return compassCategories.map(() => 0)
  }

  try {
    // First, find the highlighted user rating for this compass and framework
    const compassUserRatingsResponse = await feathersClient.service('compass-user-ratings').find({
      query: {
        compassId,
        frameworkId,
        isHighlighted: true,
      },
    })

    // Handle both response formats (array or object with data property)
    const compassUserRatings = Array.isArray(compassUserRatingsResponse)
      ? compassUserRatingsResponse
      : compassUserRatingsResponse?.data || []

    if (compassUserRatings.length === 0) {
      return compassCategories.map(() => 0)
    }

    const compassUserRatingId = compassUserRatings[0]?.id

    // Then get all category ratings for this user rating in a single request
    const categoryRatingsResponse = await feathersClient.service('compass-categories-ratings').find({
      query: {
        compassId,
        compassCategoryId: {
          $in: compassCategories.map(category => category.id),
        },
        compassUserRatingId,
      },
    })

    // Handle both response formats (array or object with data property)
    const categoryRatings = Array.isArray(categoryRatingsResponse)
      ? categoryRatingsResponse
      : categoryRatingsResponse?.data || []

    if (categoryRatings.length === 0) {
      return compassCategories.map(() => 0)
    }

    // Map the results back to the compassCategories array
    return compassCategories.map(category => {
      const rating = categoryRatings.find(r => r.compassCategoryId === category.id)
      return rating?.rating || 0
    })
  } catch (error) {
    console.error('Error getting category user ratings:', error)
    return compassCategories.map(() => 0)
  }
}
